import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { formatEuroPrice } from '@racemap/utilities/functions/billing/formatEuroPrice';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Checkbox, Col, Collapse, Flex, Row, Space, Tag, Typography } from 'antd';
import type { Immutable } from 'immer';
import type { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useCurrentDateTime } from 'src/lib/customHooks/useCurrentDateTime';
import { useGetProductPrice } from 'src/lib/customHooks/useGetProductPrice';
import { useGetProductPricePerUnit } from 'src/lib/customHooks/useGetProductPricePerUnit';
import { usePriceList } from 'src/lib/customHooks/usePriceList';
import { DatePicker } from '../BasicComponents/DateTimePicker';
import {
  IconDatafeed,
  IconDevices,
  IconKey,
  IconLeaderboard,
  IconLoads,
  IconMap,
  IconSponsor,
  IconTag,
  IconUser,
} from '../BasicComponents/Icons';
import { BillingLink } from '../BasicComponents/Links';
import { LoadingIndicator } from '../BasicComponents/LoadingIndicator';
import { PopoverHint } from '../BasicComponents/PopoverHint';
import { LoadingSpinner } from '../LoadingSpinner';
import { InputCountForm } from './InputCountForm';
import { ProductCostRow } from './ProductCostRow';
import { RangeCountForm } from './RangeCountForm';
import { getCurrentProducts } from './getCurrentProducts';
import { getTotalPrice } from './getTotalPrice';

const { Text, Title } = Typography;

interface Props {
  user: Immutable<User_Legacy> | null;
  isAdmin: boolean;
}

export const CostCalculator: FC<Props> = ({ user, isAdmin }) => {
  const currentUserId = user?.id;
  const isChildAccount = user != null && user.parentId != null;
  const currentDateTime = useCurrentDateTime(OneMinuteInMillis);
  const [productCounts, setProductCounts] = useState<Partial<Record<StripeProducts, number>>>({});
  const [isReseller, setIsReseller] = useState<boolean>(user?.isReseller || false);
  const [dateTimeOverwrite, setDateTimeOverwrite] = useState<DateTime | null>(null);
  const products = useMemo(
    () => getCurrentProducts(dateTimeOverwrite),
    [dateTimeOverwrite, currentDateTime],
  );

  useEffect(() => {
    const newCounts = structuredClone(productCounts);

    // filter the products, that are gone
    for (const product of Object.keys(newCounts)) {
      if (!products.includes(product as StripeProducts))
        delete newCounts[product as StripeProducts];
    }

    for (const product of products) {
      if (newCounts[product] != null) continue;
      switch (product) {
        case StripeProducts.API_OR_LEADERBOARD_LOAD:
          newCounts[product] = 5000;
          break;
        case StripeProducts.MAP_OR_MONITOR_LOAD:
          newCounts[product] = 1000;
          break;
        case StripeProducts.GPS_DEVICE:
          newCounts[product] = 10;
          break;
        case StripeProducts.TRANSPONDER:
          newCounts[product] = 50;
          break;
        case StripeProducts.MAP:
          newCounts[product] = 1;
          break;
        case StripeProducts.SAFEMAP:
          newCounts[product] = 0;
          break;
        default:
          newCounts[product] = 0;
          break;
      }
    }

    setProductCounts(newCounts);
  }, [products]);

  const { priceList, isLoading } = usePriceList({ isReseller });
  const getProductPrice = useGetProductPrice({ isReseller });
  const getPricePerUnit = useGetProductPricePerUnit({ isReseller });

  if (priceList == null && isLoading) return <LoadingSpinner />;
  if (getProductPrice == null || getPricePerUnit == null || isChildAccount) return <></>;

  const handleCountUpdate = (product: StripeProducts, count: number) => {
    setProductCounts({ ...productCounts, [product]: count });
  };
  const totalPrice = getTotalPrice(productCounts, priceList);

  return (
    <div style={{ padding: '1rem' }}>
      {isAdmin && (
        <>
          <Flex justify="space-around" align="center" gap={10}>
            <Space>
              <Checkbox
                checked={isReseller}
                onChange={({ target }: { target: { checked: boolean } }) => {
                  setIsReseller(target.checked);
                }}
              >
                <IconUser style={{ marginRight: '10px' }} />{' '}
                <Tag color={RacemapColors.PaleBlue}>{isReseller ? 'Reseller' : 'Normal User'}</Tag>
              </Checkbox>
            </Space>

            <Space>
              Referenze Date:
              <DatePicker
                value={dateTimeOverwrite}
                onChange={(date) => setDateTimeOverwrite(date as DateTime | null)}
                placeholder="Select date"
                allowClear
              />
            </Space>

            {isLoading && <LoadingIndicator />}
          </Flex>
          <hr />
        </>
      )}
      {isReseller && !isAdmin && (
        <>
          <Row style={{ width: '100%', justifyContent: 'center' }}>
            <>
              <IconUser style={{ marginRight: '10px' }} />{' '}
              <Tag color={RacemapColors.PaleBlue}>Reseller</Tag>
            </>
          </Row>
          <hr />
        </>
      )}
      <Row style={{ marginBottom: '3rem' }}>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.MAP] || 0}
            onChange={(value) => handleCountUpdate(StripeProducts.MAP, value)}
            icon={<IconMap className="icon-svg-black icon-copy-btn" />}
            label="Tracking Maps"
          />
        </Col>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.SAFEMAP] || 0}
            onChange={(value) => handleCountUpdate(StripeProducts.SAFEMAP, value)}
            icon={<IconMap className="icon-svg-black icon-copy-btn" />}
            label="Safemaps"
          />
        </Col>
      </Row>
      <Title level={4} style={{ marginBottom: '1rem' }}>
        Add-Ons
      </Title>
      <Row style={{ marginBottom: '3rem' }}>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.TIMING_ADDON] || 0}
            onChange={(value) => handleCountUpdate(StripeProducts.TIMING_ADDON, value)}
            max={productCounts[StripeProducts.MAP] || 1}
            icon={<IconLeaderboard className="icon-svg-black icon-copy-btn" />}
            label="Leaderboards"
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: '3rem' }}>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.SPONSOR_ADDON] || 0}
            onChange={(value) => handleCountUpdate(StripeProducts.SPONSOR_ADDON, value)}
            max={productCounts[StripeProducts.MAP] || 1}
            icon={<IconSponsor className="icon-svg-black icon-copy-btn" />}
            label="Sponsor Logos"
          />
        </Col>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.API_ADDON] || 0}
            onChange={(value) => handleCountUpdate(StripeProducts.API_ADDON, value)}
            max={productCounts[StripeProducts.MAP] || 1}
            icon={<IconDatafeed className="icon-svg-black icon-copy-btn" />}
            label="Data APIs"
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: '3rem' }}>
        <Col span={12}>
          <InputCountForm
            value={productCounts[StripeProducts.KEY] || 0}
            max={
              (productCounts[StripeProducts.GPS_DEVICE] || 0) +
              (productCounts[StripeProducts.TRANSPONDER] || 0)
            }
            onChange={(value) => handleCountUpdate(StripeProducts.KEY, value)}
            icon={<IconKey className="icon-svg-black icon-copy-btn" />}
            label="Used Keys"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {productCounts[StripeProducts.GPS_DEVICE] != null && (
            <RangeCountForm
              value={productCounts[StripeProducts.GPS_DEVICE] || 0}
              onChange={(value) => handleCountUpdate(StripeProducts.GPS_DEVICE, value)}
              max={1000}
              min={0}
              step={10}
              label={
                <Space>
                  <IconDevices className="icon-svg-black icon-copy-btn" />
                  <Text strong>GPS Devices (first 10 are free)</Text>
                  <PopoverHint title="Devices per account">
                    Devices become chargeable with sending data related to start- and finish time of
                    an activated Tracking Map (base price has been paid). A device is anything that
                    causes a dot in your Tracking Map. A device may be a smart phone, a GPS tracker,
                    Timing equipment such as a transponder, a Track Box or a decoder. A device is
                    charged for a period of 10 days from the first timestamp. If there is more data
                    different from this period additional devices become chargeable.
                  </PopoverHint>
                </Space>
              }
            />
          )}
          {productCounts[StripeProducts.TRANSPONDER] != null && (
            <RangeCountForm
              value={productCounts[StripeProducts.TRANSPONDER] || 0}
              onChange={(value) => handleCountUpdate(StripeProducts.TRANSPONDER, value)}
              max={10000}
              min={0}
              step={10}
              label={
                <Space>
                  <IconTag className="icon-svg-black icon-copy-btn" />
                  <Text strong>Transponder (first 50 are free)</Text>
                  <PopoverHint title="Devices per account">
                    Devices become chargeable with sending data related to start- and finish time of
                    an activated Tracking Map (base price has been paid). A device is anything that
                    causes a dot in your Tracking Map. A device may be a smart phone, a GPS tracker,
                    Timing equipment such as a transponder, a Track Box or a decoder. A device is
                    charged for a period of 10 days from the first timestamp. If there is more data
                    different from this period additional devices become chargeable.
                  </PopoverHint>
                </Space>
              }
            />
          )}
          {productCounts[StripeProducts.MAP_OR_MONITOR_LOAD] != null && (
            <RangeCountForm
              value={productCounts[StripeProducts.MAP_OR_MONITOR_LOAD] || 0}
              onChange={(value) => handleCountUpdate(StripeProducts.MAP_OR_MONITOR_LOAD, value)}
              max={100000}
              min={0}
              step={100}
              label={
                <Space>
                  <IconLoads className="icon-svg-black icon-copy-btn" />
                  <Text strong>Map Loads (first 1000 are free)</Text>
                  <PopoverHint title="Loads per account">
                    Each page view of Tracking Maps and each load of Leaderboards and Data APIs in
                    your account is one load. You may archive your events in order to limit loads.
                  </PopoverHint>
                </Space>
              }
            />
          )}
          {productCounts[StripeProducts.API_OR_LEADERBOARD_LOAD] != null && (
            <RangeCountForm
              value={productCounts[StripeProducts.API_OR_LEADERBOARD_LOAD] || 0}
              onChange={(value) => handleCountUpdate(StripeProducts.API_OR_LEADERBOARD_LOAD, value)}
              max={100000}
              min={0}
              step={100}
              label={
                <Space>
                  <IconLoads className="icon-svg-black icon-copy-btn" />
                  <Text strong>API or Leaderboard Loads (first 5000 are free)</Text>
                  <PopoverHint title="Loads per account">
                    Each page view of Tracking Maps and each load of Leaderboards and Data APIs in
                    your account is one load. You may archive your events in order to limit loads.
                  </PopoverHint>
                </Space>
              }
            />
          )}
        </Col>
      </Row>
      <Row style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <Col sm={10} xs={12} offset={2}>
          Total Price: <br />
          <Title level={4} style={{ marginTop: '0.5rem' }}>
            {formatEuroPrice(totalPrice)} excl. VAT
          </Title>
        </Col>
        <Col sm={10} xs={12}>
          Effective price per device: <br />
          <Title level={4} style={{ marginTop: '0.5rem' }}>
            {formatEuroPrice(
              totalPrice /
                ((productCounts[StripeProducts.GPS_DEVICE] || 0) +
                  (productCounts[StripeProducts.TRANSPONDER] || 0) || 1),
            )}{' '}
            excl. VAT
          </Title>
        </Col>
      </Row>
      {currentUserId != null && (
        <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
          Find your current billing overview on your{' '}
          <BillingLink userId={currentUserId} label="billing page" />.
        </div>
      )}
      <div style={{ background: 'white' }}>
        <Collapse
          items={[
            {
              label: 'Specific Costs',
              children: (
                <>
                  {Object.entries(productCounts)
                    .filter(([_p, c]) => c !== 0)
                    .map(([product, count], index, countArray) => (
                      <ProductCostRow
                        key={product}
                        product={product as StripeProducts}
                        quantity={count || 0}
                        price={priceList?.[product as StripeProducts]}
                        getPricePerUnit={getPricePerUnit}
                        getProductPrice={getProductPrice}
                        lastEntry={index === countArray.length - 1}
                      />
                    ))}
                </>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};
